<script setup lang="ts">import { ref as _ref } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { onMounted } from 'vue';
import { TagDto, TermService } from '@/api';
import i18n from '@/i18n';
import Term from '@/components/terms/Term.vue';
import { VAutocomplete } from 'vuetify/lib';

interface TermSelectorProps {
  hint?: string;
  returnObject?: boolean;
}
const props = defineProps({
  hint: { default: i18n.t('terms.hint') },
  returnObject: { type: Boolean, default: true },
  value: null
});

type TermSelectorEmits = {
  (event: 'input', tags: TagDto[] | null): void
}
const emits = defineEmits(["input"]);

const { value: selectedTerms } = __MACROS_useVModel(["value", undefined, "input"]);

let terms = _ref<TagDto[]>([]);
let loading = _ref(false);
const searchString = _ref('');
const termSelector = _ref<{ blur:() => void } | null>(null);

/**
 * Get different item icon based on content value
 * @param hasValue true if not empty
 */
const customItemIcon = (hasValue: boolean) => (hasValue ? 'icon-check-square-fill' : 'icon-check-square-outline-empty');

/**
 * Close v-autocomplete dropdown
 */
const closeMenu = () => {
  termSelector.value?.blur();
};

/**
 * Clear selected terms & fetched data
 */
const clear = () => {
  selectedTerms.value = [];
  emits('input', selectedTerms.value);
  closeMenu();
};

/**
 * Fetch complete term data
 */
const fetchTerms = async () => {
  loading.value = true;
  const { data } = await TermService.getAllTerm({ itemsPerPage: -1 });
  terms.value = data;
  loading.value = false;
};

onMounted(() => {
  fetchTerms();
});
</script>

<template lang="pug">
v-autocomplete.term-selector(
  ref="termSelector"
  v-model="selectedTerms"
  v-on='$listeners'
  :loading="loading"
  :items="terms"
  :search-input.sync="searchString"
  :hint="props.hint"
  :return-object="returnObject"
  @change="searchString=''"
  @click:clear="clear"
  :placeholder="$t('terms.placeholder')"
  label=""
  item-value="id"
  item-text="name"
  data-track="term-selector"
  dense
  outlined
  hide-details
  hide-no-data persistent-hint
  multiple
  clearable)

  template(v-slot:item="{ item, attrs }")
    div.full-width(data-track="term-selector-item")
      v-icon.ml-2.mr-1 {{ customItemIcon(attrs.inputValue) }}
      Term.my-3( :term="item" :key="item.id" small) {{ item.name }}

  template(v-slot:selection="{ item }")
    Term.my-1( :term="item" :key="item.id" small) {{ item.name }}

</template>

<style lang="scss">
.term-selector {
  .v-select__slot {
    padding: 3px 0px 3px;
  }
  .v-input__append-inner {
    margin-top: 5px !important;
  }
}

</style>
